import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ClientBookingV2Service } from '@fitness-force/api';
import { ScheduleType } from '@fitness-force/enum';
import type { ClassBookingV2, ClassBookingV2Detail } from '@fitness-force/models';
import { getEarlyCancelWindowEnd } from '@fitness-force/models';
import { DataGridComponent, EmptyStateContainerComponent } from '@fitness-force/grid';
import { DateFormatedComponent } from '@fitness-force/grid-utility';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, finalize } from 'rxjs';
import { StaffInfoComponent } from '../../../staff/staff-info/staff-info.component';
import { BookingStatusComponent } from '../../../data-grid/utility/booking-status/booking-status.component';
import { ClientClassBookingV2IndicatorsCellComponent } from '../client-class-booking-v2-indicators-cell/client-class-booking-v2-indicators-cell.component';
import { ClientBookingDetailDialogComponent } from '../client-booking-detail-dialog/client-booking-detail-dialog.component';
import { ClientBookingSubscriptionTypeCellComponent } from '../client-booking-subscription-type-cell/client-booking-subscription-type-cell.component';
import {
  getSubscriptionColor,
  type SubscriptionColorName,
} from '../../../../utils/client-booking-subscription-color.util';

export interface ClassBookingV2GridRow extends ClassBookingV2 {
  Type: string;
  StartDate: string;
  TypeName: string;
  CoachId: number;
  BookingStatus: string;
  EarlyCancelCloses: string | null;
  SubscriptionColor: SubscriptionColorName | '';
}

@Component({
  selector: 'client-class-booking-list-v2',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DataGridComponent, TranslateModule, ClientBookingDetailDialogComponent],
  templateUrl: './client-class-booking-list-v2.component.html',
  styleUrl: './client-class-booking-list-v2.component.scss',
})
export class ClientClassBookingListV2Component {
  private readonly bookingV2Service = inject(ClientBookingV2Service);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly clientId = input.required<number>();
  readonly tenantId = input.required<number>();
  readonly subscriptionColorMap = input<
    ReadonlyMap<number, SubscriptionColorName> | null
  >(null);

  tableData$ = new BehaviorSubject<ClassBookingV2GridRow[]>([]);
  tableColumns$ = new BehaviorSubject<unknown[]>([]);
  emptyStateComponent$ = new BehaviorSubject(EmptyStateContainerComponent);
  totalRecordLength = new BehaviorSubject({
    totalPage: 0,
    totalItems: 0,
    currentPage: 0,
  });

  loading = false;
  pageNumber = 1;
  pageSize = 10;

  detailVisible = false;
  detailLoading = false;
  selectedDetail: ClassBookingV2Detail | null = null;

  private readonly detailCache = new Map<number, ClassBookingV2Detail>();

  constructor() {
    effect(() => {
      const tenantId = this.tenantId();
      const clientId = this.clientId();
      if (tenantId > 0 && clientId > 0) {
        this.pageNumber = 1;
        this.columnTranslate();
        this.loadBookings();
      }
    });

    effect(() => {
      this.subscriptionColorMap();
      this.applySubscriptionColors();
    });
  }

  columnTranslate(): void {
    this.translate
      .get([
        'CLIENT_BOOKING.DATETIME',
        'CLIENT_BOOKING.BOOKING_TYPE',
        'CLIENT_BOOKING.TRAINER',
        'CLIENT_BOOKING.STATUS',
        'CLIENT_BOOKING.V2.INDICATORS',
        'CLIENT_BOOKING.V2.EARLY_CANCEL_CLOSES',
      ])
      .subscribe((translations) => {
        this.setColumnTranslate(translations);
        this.cdr.markForCheck();
      });
  }

  setColumnTranslate(translations: Record<string, string>): void {
    this.tableColumns$.next([
      {
        title: translations['CLIENT_BOOKING.DATETIME'],
        classes: 'py-3 white-space-nowrap align-items-center',
        dataProperty: 'StartDate',
        type: 'component',
        data: {
          name: DateFormatedComponent,
          additionData: true,
          aData: {
            KeyName: 'StartDate',
            format: 'datetime'
          },
        },
      },
      {
        title: translations['CLIENT_BOOKING.BOOKING_TYPE'],
        classes: 'py-3 white-space-nowrap',
        dataProperty: 'TypeName',
        type: 'component',
        data: { name: ClientBookingSubscriptionTypeCellComponent },
      },
      {
        title: translations['CLIENT_BOOKING.TRAINER'],
        classes: 'py-3 white-space-nowrap',
        dataProperty: 'CoachId',
        type: 'component',
        data: { name: StaffInfoComponent },
      },
      {
        title: translations['CLIENT_BOOKING.STATUS'],
        classes: 'py-3 white-space-nowrap',
        dataProperty: 'BookingStatus',
        type: 'component',
        data: { name: BookingStatusComponent },
      },
      {
        title: translations['CLIENT_BOOKING.V2.INDICATORS'],
        classes: 'py-3 white-space-nowrap',
        dataProperty: 'booking_id',
        type: 'component',
        data: { name: ClientClassBookingV2IndicatorsCellComponent },
      },
      {
        title: translations['CLIENT_BOOKING.V2.EARLY_CANCEL_CLOSES'],
        classes: 'py-3 white-space-nowrap',
        dataProperty: 'EarlyCancelCloses',
        type: 'component',
        data: {
          name: DateFormatedComponent,
          additionData: true,
          aData: {
            KeyName: 'EarlyCancelCloses',
            format: 'datetime'
          },
        },
      },
    ]);
  }

  loadBookings(): void {
    const tenantId = this.tenantId();
    const clientId = this.clientId();
    if (tenantId <= 0 || clientId <= 0) {
      return;
    }

    this.loading = true;
    this.bookingV2Service
      .listClassBookings({
        tenant_id: tenantId,
        client_id: clientId,
        pageNumber: this.pageNumber,
        pageSize: this.pageSize,
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          const rows = (response.items ?? []).map((item) =>
            this.mapClassBookingToGridRow(item),
          );
          this.tableData$.next(rows);
          this.totalRecordLength.next({
            totalPage: response.total_pages ?? 0,
            totalItems: response.total_items ?? 0,
            currentPage: response.current_page ?? this.pageNumber,
          });
        },
        error: () => {
          this.tableData$.next([]);
          this.totalRecordLength.next({
            totalPage: 0,
            totalItems: 0,
            currentPage: 0,
          });
        },
      });
  }

  fetchNextSetData(event: { ps: number; pn: number }): void {
    this.pageNumber = event.pn;
    this.pageSize = event.ps;
    this.loadBookings();
  }

  onRowClick(event: { data: ClassBookingV2GridRow }): void {
    if (event?.data?.booking_id) {
      this.openDetail(event.data);
    }
  }

  openDetail(row: ClassBookingV2GridRow): void {
    const cached = this.detailCache.get(row.booking_id);
    if (cached) {
      this.selectedDetail = cached;
      this.detailVisible = true;
      this.detailLoading = false;
      this.cdr.markForCheck();
      return;
    }

    this.detailVisible = true;
    this.detailLoading = true;
    this.selectedDetail = null;
    this.cdr.markForCheck();

    this.bookingV2Service
      .getClassBookingDetail(this.tenantId(), this.clientId(), row.booking_id)
      .pipe(
        finalize(() => {
          this.detailLoading = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (detail) => {
          this.detailCache.set(row.booking_id, detail);
          this.selectedDetail = detail;
          this.cdr.markForCheck();
        },
        error: () => {
          this.detailVisible = false;
          this.cdr.markForCheck();
        },
      });
  }

  closeDetail(): void {
    this.detailVisible = false;
    this.cdr.markForCheck();
  }

  private mapClassBookingToGridRow(
    booking: ClassBookingV2,
  ): ClassBookingV2GridRow {
    const earlyCancel = getEarlyCancelWindowEnd(
      booking.class?.start_datetime,
      booking.class?.booking_late_cancel_in_minutes,
    );
    return {
      ...booking,
      Type: ScheduleType.Class,
      StartDate: booking.class?.start_datetime ?? '',
      TypeName: booking.class?.type_name_primary_language ?? '',
      CoachId: booking.class?.coach_id ?? 0,
      BookingStatus: String(booking.booking_status ?? ''),
      EarlyCancelCloses: earlyCancel ? earlyCancel.toISOString() : null,
      SubscriptionColor:
        getSubscriptionColor(
          this.subscriptionColorMap(),
          booking.subscription_id,
        ) ?? '',
    };
  }

  private applySubscriptionColors(): void {
    const current = this.tableData$.getValue();
    if (!current.length) {
      return;
    }

    const colorMap = this.subscriptionColorMap();
    this.tableData$.next(
      current.map((row) => ({
        ...row,
        SubscriptionColor:
          getSubscriptionColor(colorMap, row.subscription_id) ?? '',
      })),
    );
  }
}
